import type { ConsumerDetailsType } from "@/types"
import { Link } from "@tanstack/react-router"
import { ColumnDef } from "@tanstack/react-table"
import { Disc, Pen } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Icons } from "@/components/icons"

import { DataTableColumnHeader } from "../../data-table"
import { ConsumerDetailsOperations } from "../operations/consumer-details-operations"

export const consumerDetailsColumns: ColumnDef<ConsumerDetailsType>[] = [
  {
    accessorKey: "acc_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Acc No." />
    ),
    cell: ({ row }) => {
      const value = row.original.acc_id
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Consumer" />
    ),
    cell: ({ row }) => {
      const value = row.original.consumer
      return (
        <Link
          to={`/manage-consumers/view-billing-history/${row.original.consumer_id}`}
          className="text-right hover:underline md:py-0 md:text-left"
        >
          {value}
        </Link>
      )
    },
  },
  {
    accessorKey: "zone",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Zone" />
    ),
    cell: ({ row }) => {
      const value = row.original.zone
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "book",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Book" />
    ),
    cell: ({ row }) => {
      const value = row.original.book
      return <div>{value}</div>
    },
  },

  {
    accessorKey: "connection_status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Connection Status" />
    ),
    cell: ({ row }) => {
      const value = row.original.connection_status

      const statusClasses: any = {
        Active: {
          base: "bg-[#e6f7e6] text-[#00C853]",
          hover: "hover:bg-[#c8e6c9]",
        },
        Inactive: {
          base: "bg-[#fffde0] text-[#d4af00]",
          hover: "hover:bg-[#fff8b0]",
        },
        Delinquent: {
          base: "bg-[#fff0cc] text-[#F8961E]",
          hover: "hover:bg-[#ffe0b2]",
        },
        Disconnected: {
          base: "bg-[#ffebee] text-[#F44336]",
          hover: "hover:bg-[#ffcdd2]",
        },
        Unassigned: {
          base: "bg-[#fff8e1] text-[#8D6E63]",
          hover: "hover:bg-[#fff0c2]",
        },
        "Before Due": {
          base: "bg-[#f3e5f5] text-[#9C27B0]",
          hover: "hover:bg-[#e1bee7]",
        },
        "Due Today": {
          base: "bg-[#fce4ec] text-[#E91E63]",
          hover: "hover:bg-[#f8bbd0]",
        },
        "Over Due": {
          base: "bg-[#f8e1ea] text-[#AD1457]",
          hover: "hover:bg-[#f48fb1]",
        },
        Disconnection: {
          base: "bg-[#ede7f6] text-[#673AB7]",
          hover: "hover:bg-[#d1c4e9]",
        },
        Pending: {
          base: "bg-[#e3f2fd] text-[#2196F3]",
          hover: "hover:bg-[#bbdefb]",
        },
      }

      const currentStatus = statusClasses[value] || {
        base: "bg-gray-100 text-gray-600",
        hover: "hover:bg-gray-200",
      }

      return (
        <Badge
          className={`px-2 py-1 transition-colors duration-200 print:text-xl ${
            currentStatus.base
          } ${currentStatus.hover}`}
        >
          {value}
        </Badge>
      )
    },
  },
  {
    accessorKey: "connection_type",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Connection Type" />
    ),
    cell: ({ row }) => {
      const value = row.original.connection_type
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "address",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="justify-center"
        column={column}
        title="Address"
      />
    ),
    cell: ({ row }) => {
      const value = row.original.address
      return (
        <div
          className="max-h-[70px] w-[420px] max-w-[420px] overflow-auto text-right md:max-w-full md:py-0 md:text-left"
          title={value}
        >
          {value}
        </div>
      )
    },
  },
  {
    accessorKey: "meter_num",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="  Meter No." />
    ),
    cell: ({ row }) => {
      const value = row.original.meter_num
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "contact_num",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Contact No." />
    ),
    cell: ({ row }) => {
      const value = row.original.contact_num
      return <div>{value}</div>
    },
  },
  {
    accessorKey: "actions",
    header: () => {
      return <div className="text-center dark:text-white">Actions</div>
    },
    cell: ({ row }) => {
      return (
        <div className="px-4 py-2 text-center md:py-0 md:text-center">
          <ConsumerDetailsOperations data={row.original} />
        </div>
      )
    },
  },
]
